import React from "react";
import { GlobalContext } from "../App";
import { api_getPrices, api_getAvailability, api_createBooking } from "../api/Api";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

export function BookPanel() {

    const { isMobile } = React.useContext(GlobalContext);

    const [prices, setPrices] = React.useState([]);
    const [selectedDuration, setSelectedDuration] = React.useState(null);
    const [allSlots, setAllSlots] = React.useState([]);
    const [selectedDate, setSelectedDate] = React.useState(null);
    const [selectedSlot, setSelectedSlot] = React.useState(null);
    const [studentName, setStudentName] = React.useState("");
    const [studentEmail, setStudentEmail] = React.useState("");
    const [discountCode, setDiscountCode] = React.useState("");
    const [processing, setProcessing] = React.useState(false);

    const [formStatus, setFormStatus] = React.useState({
        message: ""
    });

    function durationLabel(duration) {

        switch (duration) {

            case "THIRTY_MINUTES":
                return "30 minutes";

            case "FORTY_FIVE_MINUTES":
                return "45 minutes";

            case "SIXTY_MINUTES":
                return "60 minutes";

            default:
                return duration;
        }
    }

    function formatDateForApi(date) {

        return (
            date.getFullYear() +
            "-" +
            String(date.getMonth() + 1).padStart(2, "0") +
            "-" +
            String(date.getDate()).padStart(2, "0")
        );

    }

    React.useEffect(() => {

        api_getPrices(setFormStatus, setPrices);

    }, []);

    React.useEffect(() => {

        if (!selectedDuration) {
            return;
        }

        api_getAvailability(
            selectedDuration,
            setFormStatus,
            setAllSlots
        );

    }, [selectedDuration]);

    const availableDates = React.useMemo(() => {
        const dates = new Set();
        allSlots.forEach(slot => {
            const date = new Date(slot.start);
            dates.add(date.toLocaleDateString("en-CA"));
        });
        return [...dates];
    }, [allSlots]);

    const slots = React.useMemo(() => {
        if (!selectedDate) {
            return [];
        }
        const selected =
            selectedDate.toLocaleDateString("en-CA");
        return allSlots.filter(slot =>
            new Date(slot.start)
                .toLocaleDateString("en-CA") === selected

        );

    }, [allSlots, selectedDate]);

    function formatSlotTime(value) {

        const date = new Date(value);

        return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit"
        });

    }

    function formatPrice(value) {
        return Number(value).toFixed(2);
    }


    function getSelectedPrice() {
        const price = prices.find(
            p => p.duration === selectedDuration
        );
        return price ? price.price : 0;
    }

    function validateEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    function continueToPayment() {

        setFormStatus({
            message: ""
        });

        if (!studentName.trim()) {
            setFormStatus({
                message: "Please enter your name."
            });
            return;
        }

        if (!studentEmail.trim()) {
            setFormStatus({
                message: "Please enter your email address."
            });
            return;
        }

        if (!validateEmail(studentEmail)) {
            setFormStatus({
                message: "Please enter a valid email address."
            });
            return;
        }

        setProcessing(true);

        const booking = {
            studentName: studentName.trim(),
            studentEmail: studentEmail.trim(),
            start: selectedSlot.start,
            duration: selectedDuration,
            discountCode: discountCode.trim()
        };

        api_createBooking(
            booking,
            setFormStatus,
            (payment) => {
                window.location.href = payment.approvalUrl;
            },
            setProcessing
        );
    }

    function clearFormStatus() {

        setFormStatus({
            message: "",
            type: ""
        });

    }

    return (
        <div className="content-inner main-block content-wide">

            <div className={isMobile ? "wide" : "narrow"}>

                <h1>Booking calendar</h1>
                <h2>Please only use this form to rebook. If you have not yet had an introductory lesson, use the <a href="/#contact" className="booking-contact-link">contact me</a> form to arrange.</h2>

                {formStatus.message !== "" &&
                    <p className={`booking-form-message ${formStatus.type || "error"}`}>
                        {formStatus.message}
                    </p>
                }

                <div className="booking-duration-list">

                    {prices.map(price => (

                        <button
                            key={price.duration}
                            className={
                                selectedDuration === price.duration
                                    ? "booking-duration selected"
                                    : "booking-duration"
                            }
                            onClick={() => {
                                setSelectedDuration(price.duration);
                                setSelectedDate(null);
                                setSelectedSlot(null);
                                clearFormStatus();
                            }}
                        >

                            <span>
                                {selectedDuration === price.duration ? "✓ " : ""}
                                {durationLabel(price.duration)} lesson
                            </span>

                            <span className="price">
                                £{Number(price.price).toFixed(2)}
                            </span>

                        </button>

                    ))}

                </div>

                {selectedDuration &&
                    <div className="booking-calendar-container">
                        <div className="booking-calendar">

                            <h2>Select a date</h2>

                            <Calendar
                                onChange={date => {
                                    setSelectedDate(date);
                                    setSelectedSlot(null);
                                    clearFormStatus();
                                }}
                                value={selectedDate}
                                tileDisabled={({ date }) => {
                                    const iso = formatDateForApi(date);
                                    return !availableDates.includes(iso);
                                }}
                            />

                        </div>

                        <div className="booking-slot-list">

                            <h2>Select a time</h2>

                            {!selectedDate ? (

                                <p className="booking-no-slots">
                                    Please select a day.
                                </p>

                            ) : slots.length === 0 ? (

                                <p className="booking-no-slots">
                                    Sorry, there are no available times on this date.
                                    Please choose another date.
                                </p>

                            ) : (

                                <div className="booking-slot-grid">

                                    {slots.map(slot => (
                                        <button
                                            key={slot.start}
                                            className={
                                                selectedSlot?.start === slot.start
                                                    ? "booking-slot selected"
                                                    : "booking-slot"
                                            }
                                            onClick={() => {
                                                setSelectedSlot(slot);
                                                clearFormStatus();
                                            }}
                                        >
                                            {formatSlotTime(slot.start)}
                                        </button>
                                    ))}

                                </div>

                            )}

                        </div>
                    </div>
                }
                {selectedSlot &&
                    <div className="booking-details">
                        <div className="booking-details-form">
                            <h2>Your details</h2>
                            <input
                                type="text"
                                placeholder="Your name"
                                value={studentName}
                                onChange={e => setStudentName(e.target.value)}
                                required
                            />
                            <input
                                type="email"
                                placeholder="Email address"
                                value={studentEmail}
                                onChange={e => setStudentEmail(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Discount code (optional)"
                                value={discountCode}
                                onChange={e => setDiscountCode(e.target.value)}
                            />
                        </div>

                        <div className="booking-summary">
                            <h3>Summary</h3>
                            <p>
                                {durationLabel(selectedDuration)} lesson
                            </p>
                            <p>
                                {selectedDate && selectedDate.toLocaleDateString()}
                            </p>
                            <p>
                                {selectedSlot && formatSlotTime(selectedSlot.start)}
                            </p>
                            <p>
                                Price: £{formatPrice(getSelectedPrice())}
                            </p>
                        </div>

                        <button
                            onClick={continueToPayment}
                            disabled={processing}
                        >
                            {processing ? "Creating payment..." : "Continue to payment"}
                        </button>

                    </div>

                }
            </div>

        </div>
    );
}